import { Icon } from "@iconify/react";
import React from "react";
import { Link } from "react-router-dom";
import SEOHead from "../../components/tools/SEOHead";
import MarketingShell, { bookingHref } from "../MarketingShell";

const benchmarkResults = [
  {
    name: "LoCoMo",
    description: "Long conversational memory",
    score: 94.6,
    metric: "Accuracy",
  },
  {
    name: "LongMemEval",
    description: "Long-term memory reasoning",
    score: 97.2,
    metric: "Accuracy",
  },
  {
    name: "BEAM 1M",
    description: "One-million-token history",
    score: 68.1,
    metric: "Average score × 100",
  },
  {
    name: "BEAM 10M",
    description: "Ten-million-token history",
    score: 52.1,
    metric: "Average score × 100",
  },
];

const architectureLayers = [
  {
    number: "01",
    title: "Shared user memory",
    label: "Cross-agent",
    description:
      "Stable facts and standing instructions are stored once and made available across a user's agents, so every workflow starts with the same identity and preferences.",
  },
  {
    number: "02",
    title: "Agent-specific learning",
    label: "Per agent",
    description:
      "Each agent keeps the preferences it learns for its own role. This prevents a writing preference, research habit, or project convention from polluting unrelated work.",
  },
  {
    number: "03",
    title: "Updateable working memory",
    label: "Curated context",
    description:
      "Stored facts support add, update, and remove operations, so corrected information can replace stale context. Consolidation logic can merge and prune memories as the collection grows.",
  },
  {
    number: "04",
    title: "Source-backed recall",
    label: "Cited evidence",
    description:
      "Meetings and documents stay in the evidence layer. The agent retrieves the relevant passage only when needed and returns the exact timestamp, paragraph, or page with the answer.",
  },
];

const faqItems = [
  {
    question: "What is an AI memory benchmark?",
    answer:
      "An AI memory benchmark measures whether a system can retain, retrieve, and reason over information from long or multi-session histories. The suites on this page test factual recall, temporal reasoning, knowledge updates, preferences, contradiction handling, and abstention.",
  },
  {
    question: "What do LoCoMo, LongMemEval, and BEAM test?",
    answer:
      "LoCoMo uses long multi-session conversations. LongMemEval contains 500 questions testing five core abilities: extraction, multi-session and temporal reasoning, knowledge updates, and abstention. BEAM tests ten memory abilities across histories ranging from 100,000 to 10 million tokens.",
  },
  {
    question: "How does YouWo approach long-term memory?",
    answer:
      "YouWo separates shared user facts, agent-specific preferences, compact working memory, and source evidence. Durable context stays close to the agent while deeper documents and recordings are retrieved only when a task needs them.",
  },
  {
    question: "Can benchmark scores be compared directly?",
    answer:
      "Only when the complete evaluation configuration is understood. Embeddings, extraction model, answerer, judge, retrieval depth, and prompts can all change the result. This page does not present YouWo's preliminary values as a direct comparison with another system.",
  },
];

const structuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "TechArticle",
      headline: "Building AI Memory That Holds Up at 10 Million Tokens",
      description:
        "YouWo's LoCoMo, LongMemEval, and BEAM memory benchmark results, evaluation method, and long-term AI memory architecture.",
      datePublished: "2026-08-02",
      dateModified: "2026-09-26",
      author: { "@type": "Organization", name: "YouWo Research" },
      publisher: {
        "@type": "Organization",
        name: "YouWo",
        logo: { "@type": "ImageObject", url: "https://youwo.ai/logo/logo.png" },
      },
      mainEntityOfPage: "https://youwo.ai/research/memory-benchmarks",
      citation: ["https://arxiv.org/abs/2402.17753", "https://arxiv.org/abs/2410.10813", "https://arxiv.org/abs/2510.27246"],
      keywords: [
        "AI memory benchmark",
        "LoCoMo",
        "LongMemEval",
        "BEAM benchmark",
        "long-term AI memory",
        "agent memory",
      ],
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://youwo.ai/" },
        { "@type": "ListItem", position: 2, name: "Research", item: "https://youwo.ai/research" },
        {
          "@type": "ListItem",
          position: 3,
          name: "Memory Benchmarks",
          item: "https://youwo.ai/research/memory-benchmarks",
        },
      ],
    },
    {
      "@type": "FAQPage",
      mainEntity: faqItems.map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: { "@type": "Answer", text: item.answer },
      })),
    },
  ],
};

const MemoryBenchmarks: React.FC = () => {
  return (
    <MarketingShell><div className="min-h-screen bg-white font-roboto text-[#172033] selection:bg-blue-100 selection:text-blue-900">
      <SEOHead
        title="YouWo Memory Benchmarks: LoCoMo, LongMemEval & BEAM"
        description="Read YouWo's preliminary internal LoCoMo, LongMemEval, and BEAM memory benchmark results, including histories up to 10 million tokens."
        keywords="AI memory benchmark, LoCoMo benchmark, LongMemEval, BEAM benchmark, long-term AI memory, agent memory, YouWo memory"
        canonicalUrl="https://youwo.ai/research/memory-benchmarks"
        ogImage="https://youwo.ai/landing/display.png"
        ogType="article"
        structuredData={structuredData}
      />

      <main id="main-content" tabIndex={-1}>
        <article>
          <header className="relative overflow-hidden border-b border-[#e8eef5] bg-gradient-to-b from-[#f4f9ff] to-white px-4 pb-20 pt-32 md:pb-28 md:pt-40">
            <div className="pointer-events-none absolute -right-32 top-20 h-96 w-96 rounded-full bg-[#dff1ff] opacity-70 blur-3xl" />
            <div className="pointer-events-none absolute -left-36 bottom-0 h-80 w-80 rounded-full bg-[#e3fff5] opacity-70 blur-3xl" />

            <div className="relative mx-auto max-w-5xl">
              <nav aria-label="Breadcrumb" className="mb-10 flex items-center gap-2 text-sm text-[#66768a]">
                <Link to="/" className="text-[#66768a] no-underline hover:text-cornflowerblue">
                  Home
                </Link>
                <Icon icon="lucide:chevron-right" className="text-xs" />
                <span>Research</span>
                <Icon icon="lucide:chevron-right" className="text-xs" />
                <span className="text-[#172033]">Memory benchmarks</span>
              </nav>

              <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#cce3fa] bg-white px-4 py-2 text-sm font-semibold text-[#287abf] shadow-sm">
                <span className="h-2 w-2 rounded-full bg-cornflowerblue" />
                YouWo Research
              </div>

              <h1 className="m-0 max-w-4xl text-4xl font-bold leading-[1.08] tracking-[-0.035em] text-[#111827] md:text-6xl lg:text-[68px]">
                Building AI memory that holds up at 10 million tokens
              </h1>
              <p className="mb-0 mt-7 max-w-3xl text-lg leading-8 text-[#55657a] md:text-xl md:leading-9">
                YouWo reports preliminary internal scores of 94.6 on LoCoMo, 97.2 on LongMemEval, 68.1 on BEAM 1M, and 52.1 on BEAM 10M. Here is what those suites test, how the reported evaluation was structured, and how the production system approaches memory.
              </p>

              <div className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-[#66768a]">
                <span className="font-semibold text-[#354256]">By YouWo Research</span>
                <span>August 2, 2026</span>
                <span>9 min read</span>
              </div>

              <div className="mt-10 flex flex-wrap gap-3">
                <a
                  href="#results"
                  className="inline-flex items-center gap-2 rounded-full bg-cornflowerblue px-6 py-3 font-semibold text-white no-underline shadow-[0_10px_30px_rgba(78,164,245,0.28)] transition-transform hover:-translate-y-0.5"
                >
                  View the results
                  <Icon icon="lucide:arrow-down" />
                </a>
                <a
                  href="https://github.com/mem0ai/memory-benchmarks"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full border border-[#d7e0e9] bg-white px-6 py-3 font-semibold text-[#354256] no-underline transition-colors hover:border-cornflowerblue hover:text-cornflowerblue"
                >
                  Public benchmark suite
                  <Icon icon="lucide:external-link" />
                </a>
              </div>
            </div>
          </header>

          <section id="results" className="scroll-mt-28 bg-[#f7f9fc] px-4 py-16 md:py-24">
            <div className="mx-auto max-w-6xl overflow-hidden rounded-[28px] border border-[#dfe7ef] bg-white shadow-[0_24px_70px_rgba(47,75,105,0.09)]">
              <div className="flex flex-col gap-3 border-b border-[#e8eef5] px-6 py-7 md:flex-row md:items-end md:justify-between md:px-10">
                <div>
                  <p className="m-0 text-sm font-bold uppercase tracking-[0.14em] text-cornflowerblue">Preliminary reported results</p>
                  <h2 className="mb-0 mt-2 text-2xl font-bold tracking-[-0.02em] text-[#111827] md:text-3xl">YouWo memory benchmark results</h2>
                </div>
                <span className="text-sm text-[#66768a]">Published August 2026</span>
              </div>

              <div className="divide-y divide-[#e8eef5]">
                {benchmarkResults.map((result) => (
                  <div key={result.name} className="grid gap-6 px-6 py-7 md:grid-cols-[1.25fr_0.55fr_1.7fr] md:items-center md:px-10">
                    <div>
                      <h3 className="m-0 text-lg font-bold text-[#172033]">{result.name}</h3>
                      <p className="mb-0 mt-1 text-sm text-[#66768a]">{result.description}</p>
                    </div>

                    <div>
                      <p className="m-0 text-xs font-bold uppercase tracking-[0.12em] text-[#66768a]">YouWo</p>
                      <p className="mb-0 mt-1 text-3xl font-bold text-cornflowerblue">{result.score}</p>
                    </div>

                    <div>
                      <div className="mb-2 flex items-center justify-between text-xs font-semibold text-[#66768a]">
                        <span>{result.metric}</span>
                        <span>{result.score} / 100</span>
                      </div>
                      <div className="h-2.5 overflow-hidden rounded-full bg-[#e9eef4]">
                        <div className="h-full rounded-full bg-gradient-to-r from-cornflowerblue to-[#69d6c5]" style={{ width: `${result.score}%` }} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t border-[#e8eef5] bg-[#fbfcfe] px-6 py-5 text-sm leading-6 text-[#66768a] md:px-10">
                These are founder-supplied preliminary values, not independently verified results or an audited comparison with another system. The public repository linked below does not include a YouWo adapter, exact configuration, or per-question outputs. BEAM averages are displayed on a 100-point scale.
              </div>
              <div className="border-t border-[#e8eef5] px-6 py-6 text-sm leading-6 text-[#526276] md:px-10">
                <h3 className="m-0 text-base font-bold text-[#172033]">Benchmark history</h3>
                <p className="mb-0 mt-2"><strong>August 2, 2026 · Preliminary internal snapshot.</strong> LoCoMo 94.6/100, LongMemEval 97.2/100, BEAM 1M 68.1/100, BEAM 10M 52.1/100. These are answer scores from the document-search evaluation described below; they are not a 99% enterprise-data retrieval measurement.</p>
                <p className="mb-0 mt-2">No later YouWo run or reproducible run bundle is published here yet. Future entries need the dataset revision, metric, model and judge settings, retrieval configuration, and per-question outputs so readers can compare changes over time.</p>
              </div>
            </div>
          </section>

          <div className="px-4 py-20 md:py-28">
            <div className="mx-auto grid max-w-6xl gap-14 lg:grid-cols-[220px_minmax(0,760px)] lg:justify-between">
              <aside className="hidden lg:block">
                <nav aria-label="Article contents" className="sticky top-28 border-l border-[#dfe7ef] pl-5">
                  <p className="mb-4 mt-0 text-xs font-bold uppercase tracking-[0.14em] text-[#66768a]">In this article</p>
                  <div className="flex flex-col gap-3 text-sm">
                    {[
                      ["summary", "What we found"],
                      ["benchmarks", "What the tests measure"],
                      ["architecture", "Memory architecture"],
                      ["methodology", "Evaluation method"],
                      ["product", "Product impact"],
                      ["limitations", "How to read the results"],
                      ["faq", "FAQ"],
                    ].map(([id, label]) => (
                      <a key={id} href={`#${id}`} className="text-[#66768a] no-underline hover:text-cornflowerblue">
                        {label}
                      </a>
                    ))}
                  </div>
                </nav>
              </aside>

              <div className="min-w-0 space-y-20">
                <section id="summary" className="scroll-mt-28">
                  <SectionLabel number="01" label="Summary" />
                  <ArticleHeading>Memory quality is more than retrieval accuracy</ArticleHeading>
                  <Lead>
                    An agent can retrieve a relevant sentence and still fail the task. Useful memory must preserve identity, recognize when knowledge changed, connect facts across sessions, order events correctly, and know when the history does not contain an answer.
                  </Lead>
                  <Paragraph>
                    The reported evaluation covers three complementary public suites instead of one headline metric. LoCoMo tests long conversational recall. LongMemEval tests structured long-term memory questions. BEAM pushes retrieval and reasoning into million-token histories.
                  </Paragraph>

                  <div className="mt-9 grid gap-4 sm:grid-cols-3">
                    {[
                      ["94.6", "LoCoMo accuracy"],
                      ["97.2", "LongMemEval accuracy"],
                      ["10M", "Largest history tested"],
                    ].map(([value, label]) => (
                      <div key={label} className="border-l-2 border-cornflowerblue bg-[#f7fbff] px-5 py-5">
                        <strong className="block text-3xl text-[#172033]">{value}</strong>
                        <span className="mt-1 block text-sm text-[#66768a]">{label}</span>
                      </div>
                    ))}
                  </div>

                  <blockquote className="mb-0 mt-10 border-l-4 border-cornflowerblue bg-[#f3f9ff] px-7 py-6 text-xl font-semibold leading-8 text-[#27415e]">
                    At 10 million tokens, memory stops being a “put more text in the prompt” problem. It becomes a context selection and knowledge maintenance problem.
                  </blockquote>
                </section>

                <section id="benchmarks" className="scroll-mt-28">
                  <SectionLabel number="02" label="The test suite" />
                  <ArticleHeading>What LoCoMo, LongMemEval, and BEAM measure</ArticleHeading>
                  <Paragraph>
                    No single benchmark captures memory quality. These suites fail systems in different ways: forgetting a fact, missing a later correction, confusing event order, retrieving plausible but irrelevant text, or answering when the system should abstain.
                  </Paragraph>

                  <div className="mt-8 divide-y divide-[#e6edf4] border-y border-[#e6edf4]">
                    <Definition
                      name="LoCoMo"
                      text="10 long, multi-session dialogues with questions covering factual recall, temporal reasoning, and multi-hop inference."
                    />
                    <Definition
                      name="LongMemEval"
                      text="500 questions testing five core abilities, including information extraction, multi-session and temporal reasoning, knowledge updates, and abstention."
                    />
                    <Definition
                      name="BEAM"
                      text="More than 2,000 questions across histories from 100,000 to 10 million tokens and ten memory abilities, including extraction, ordering, contradiction resolution, preference following, and abstention."
                    />
                  </div>
                </section>

                <section id="architecture" className="scroll-mt-28">
                  <SectionLabel number="03" label="Architecture" />
                  <ArticleHeading>How YouWo keeps memory useful instead of merely large</ArticleHeading>
                  <Lead>
                    YouWo separates durable knowledge from temporary conversation state and from the original evidence. Compact context stays close to the agent; deeper documents and recordings are retrieved only when a task needs them.
                  </Lead>
                  <Paragraph>
                    This production architecture is included as system context. The reported benchmark values do not isolate or verify every layer below, so the architecture should not be read as a causal explanation of the scores.
                  </Paragraph>

                  <div className="mt-9 overflow-hidden rounded-2xl border border-[#dfe7ef]">
                    {architectureLayers.map((layer) => (
                      <div key={layer.number} className="grid gap-4 border-b border-[#e6edf4] p-6 last:border-b-0 md:grid-cols-[48px_190px_1fr] md:items-start md:p-7">
                        <span className="text-sm font-bold text-cornflowerblue">{layer.number}</span>
                        <div>
                          <h3 className="m-0 text-base font-bold text-[#172033]">{layer.title}</h3>
                          <span className="mt-1 block text-xs font-semibold uppercase tracking-[0.1em] text-[#66768a]">{layer.label}</span>
                        </div>
                        <p className="m-0 text-[15px] leading-7 text-[#66768a]">{layer.description}</p>
                      </div>
                    ))}
                  </div>

                  <h3 className="mb-3 mt-9 text-xl font-bold text-[#172033]">Bounded updates, not unlimited accumulation</h3>
                  <Paragraph>
                    Facts carry confidence; instructions carry priority and scope. The memory service can add, update, or remove facts, while consolidation logic is designed to merge duplicates and prune stale context. That keeps the memory surface smaller and more useful than a second unbounded transcript.
                  </Paragraph>
                </section>

                <section id="methodology" className="scroll-mt-28">
                  <SectionLabel number="04" label="Evaluation method" />
                  <ArticleHeading>How the reported evaluation was structured</ArticleHeading>
                  <Paragraph>
                    The internal evaluation represented benchmark conversation histories as searchable YouWo documents or notes, retrieved relevant material for each held-out question, generated an answer from that context, and judged it against the benchmark ground truth. It exercised YouWo's document search path rather than an end-to-end third-party conversation-memory service.
                  </Paragraph>
                  <Paragraph>
                    The YouWo adapter, dataset revisions, model and judge settings, retrieval depth, prompts, and per-question outputs have not yet been published. Until those artifacts are available, these figures are reported results, not a reproducible benchmark release.
                  </Paragraph>

                  <div className="mt-8 grid gap-px overflow-hidden rounded-2xl border border-[#dfe7ef] bg-[#dfe7ef] sm:grid-cols-3">
                    {[
                      ["01", "Represent", "Convert the benchmark history into searchable YouWo documents or notes."],
                      ["02", "Search", "Retrieve the document evidence relevant to each held-out question."],
                      ["03", "Evaluate", "Generate an answer, then judge it against benchmark ground truth."],
                    ].map(([number, title, copy]) => (
                      <div key={number} className="bg-white p-6">
                        <span className="text-xs font-bold tracking-[0.14em] text-cornflowerblue">{number} / 03</span>
                        <h3 className="mb-2 mt-5 text-lg font-bold text-[#172033]">{title}</h3>
                        <p className="m-0 text-sm leading-6 text-[#66768a]">{copy}</p>
                      </div>
                    ))}
                  </div>

                  <Paragraph className="mt-7">
                    LoCoMo and LongMemEval are reported as scores out of 100. For BEAM, the average score is multiplied by 100 so every result can share the same visual scale; it is not a BEAM pass rate.
                  </Paragraph>
                </section>

                <section id="product" className="scroll-mt-28">
                  <SectionLabel number="05" label="Product impact" />
                  <ArticleHeading>Why this matters inside the YouWo app</ArticleHeading>
                  <Paragraph>
                    YouWo connects meetings, recordings, documents, links, inboxes, calendars, and workflows. The practical test of memory is whether an agent can return to that context later, understand what changed, and produce work that is still traceable to the source.
                  </Paragraph>
                  <Paragraph>
                    Stronger memory improves more than chat recall. It helps the agent preserve a user's preferences, connect a decision in one meeting to evidence in a document, maintain a workflow across sessions, and cite the exact timestamp or paragraph behind an answer.
                  </Paragraph>

                  <div className="mt-9 rounded-[24px] bg-[#172033] p-7 text-white md:p-10">
                    <span className="text-sm font-bold uppercase tracking-[0.14em] text-[#82c3ff]">From benchmark to daily work</span>
                    <h3 className="mb-3 mt-4 text-2xl font-bold md:text-3xl">Give your agent context it can prove.</h3>
                    <p className="mb-7 mt-0 max-w-2xl leading-7 text-[#cbd6e3]">
                      Record a meeting or upload a document. YouWo turns it into searchable, source-backed context the agent can carry into the next task.
                    </p>
                    <a
                      href={bookingHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 rounded-full bg-cornflowerblue px-6 py-3 font-semibold text-white no-underline"
                    >
                      Get Started
                      <Icon icon="lucide:arrow-right" />
                    </a>
                  </div>
                </section>

                <section id="limitations" className="scroll-mt-28">
                  <SectionLabel number="06" label="Responsible comparison" />
                  <ArticleHeading>How to read these results</ArticleHeading>
                  <Paragraph>
                    Benchmark numbers are evidence, not an absolute ranking of production systems. Embedding quality, extraction model, answerer, judge, retrieval depth, and prompt configuration can all affect the result.
                  </Paragraph>

                  <div className="my-8 rounded-2xl border border-[#f0dcae] bg-[#fffbf2] p-6">
                    <div className="flex items-center gap-2 font-bold text-[#74561c]">
                      <Icon icon="lucide:info" />
                      Comparison note
                    </div>
                    <p className="mb-0 mt-3 text-sm leading-7 text-[#6f6248]">
                      YouWo's 94.6, 97.2, 68.1, and 52.1 values are founder-supplied preliminary internal runs. Mem0 publishes separate reference results, but they are not presented here as a direct comparison. An apples-to-apples claim requires matching evaluation configuration and per-question artifacts.
                    </p>
                  </div>

                  <Paragraph>
                    We make that distinction visible because a memory system should be inspectable in both architecture and evaluation. Publishing the YouWo adapter, exact runner configuration, and per-question outputs is the next step for a reproducible comparison.
                  </Paragraph>
                </section>

                <section id="faq" className="scroll-mt-28">
                  <SectionLabel number="07" label="FAQ" />
                  <ArticleHeading>Frequently asked questions about AI memory benchmarks</ArticleHeading>
                  <div className="mt-8 divide-y divide-[#e6edf4] border-y border-[#e6edf4]">
                    {faqItems.map((item) => (
                      <details key={item.question} className="group py-5">
                        <summary className="flex cursor-pointer list-none items-center justify-between gap-5 font-bold text-[#172033]">
                          {item.question}
                          <Icon icon="lucide:plus" className="shrink-0 text-cornflowerblue transition-transform group-open:rotate-45" />
                        </summary>
                        <p className="mb-0 mt-4 max-w-2xl text-[15px] leading-7 text-[#66768a]">{item.answer}</p>
                      </details>
                    ))}
                  </div>

                  <div className="mt-10 rounded-2xl bg-[#f7f9fc] p-6">
                    <h3 className="m-0 text-lg font-bold text-[#172033]">Source and further reading</h3>
                    <p className="mb-0 mt-3 text-sm leading-6 text-[#66768a]">
                      Read the original <a href="https://arxiv.org/abs/2402.17753" target="_blank" rel="noopener noreferrer" className="font-semibold text-cornflowerblue">LoCoMo paper</a>, <a href="https://arxiv.org/abs/2410.10813" target="_blank" rel="noopener noreferrer" className="font-semibold text-cornflowerblue">LongMemEval paper</a>, and <a href="https://arxiv.org/abs/2510.27246" target="_blank" rel="noopener noreferrer" className="font-semibold text-cornflowerblue">BEAM paper</a>. The <a href="https://github.com/mem0ai/memory-benchmarks" target="_blank" rel="noopener noreferrer" className="font-semibold text-cornflowerblue">public benchmark suite</a> does not currently contain a YouWo adapter or the artifacts behind the values reported here.
                    </p>
                    <p className="mb-0 mt-3 text-sm leading-6 text-[#66768a]">For the next learning evaluation, <a href="https://openreview.net/pdf?id=DT7JyQC3MR" target="_blank" rel="noopener noreferrer" className="font-semibold text-cornflowerblue">MemoryAgentBench</a> offers separate tests for retrieval, learning from new examples, long-range understanding, and correcting stale memory. We have not published a YouWo result on that suite.</p>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </article>

        <section className="border-t border-[#e6edf4] bg-[#f7f9fc] px-4 py-16">
          <div className="mx-auto flex max-w-6xl flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="m-0 text-sm font-bold uppercase tracking-[0.14em] text-cornflowerblue">Keep exploring</p>
              <h2 className="mb-0 mt-2 text-2xl font-bold text-[#172033]">Turn source-backed context into finished work.</h2>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link to="/tools/ai-note-taking" className="rounded-full border border-[#d7e0e9] bg-white px-5 py-2.5 font-semibold text-[#354256] no-underline hover:border-cornflowerblue hover:text-cornflowerblue">
                AI Note Taking
              </Link>
              <Link to="/tools/meeting-notes" className="rounded-full border border-[#d7e0e9] bg-white px-5 py-2.5 font-semibold text-[#354256] no-underline hover:border-cornflowerblue hover:text-cornflowerblue">
                Meeting Notes
              </Link>
              <Link to="/tools/pdf-summarizer" className="rounded-full border border-[#d7e0e9] bg-white px-5 py-2.5 font-semibold text-[#354256] no-underline hover:border-cornflowerblue hover:text-cornflowerblue">
                PDF Summarizer
              </Link>
            </div>
          </div>
        </section>
      </main>

    </div></MarketingShell>
  );
};

const SectionLabel: React.FC<{ number: string; label: string }> = ({ number, label }) => (
  <span className="mb-4 block text-xs font-bold uppercase tracking-[0.15em] text-cornflowerblue">
    {number} · {label}
  </span>
);

const ArticleHeading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h2 className="mb-6 mt-0 text-3xl font-bold leading-tight tracking-[-0.025em] text-[#111827] md:text-4xl">{children}</h2>
);

const Lead: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="mb-5 mt-0 text-lg leading-8 text-[#405168] md:text-xl md:leading-9">{children}</p>
);

const Paragraph: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = "" }) => (
  <p className={`mb-0 mt-5 text-base leading-8 text-[#66768a] ${className}`}>{children}</p>
);

const Definition: React.FC<{ name: string; text: string }> = ({ name, text }) => (
  <div className="grid gap-2 py-6 sm:grid-cols-[140px_1fr] sm:gap-8">
    <h3 className="m-0 text-lg font-bold text-[#172033]">{name}</h3>
    <p className="m-0 text-[15px] leading-7 text-[#66768a]">{text}</p>
  </div>
);

export default MemoryBenchmarks;
